import React, { useEffect, useState } from 'react';
import './LandingAdmin.css';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase';
import useLandingSettings from '../../hooks/useLandingSettings';
import defaultBbtsConfig from '../../config/landing.config';

const randomId = (prefix = 'item') => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const emptyFeature = () => ({
  id: randomId('feature'),
  title: '',
  desc: '',
  mediaType: 'image',
  media: '',
});

const emptyTestimonial = () => ({
  id: randomId('testimonial'),
  name: '',
  text: '',
});

const emptyService = () => ({
  id: randomId('service'),
  title: '',
  desc: '',
  src: '',
  gallery: [],
});

const cloneServices = (services = []) =>
  services.map((svc) => ({
    id: svc.id || randomId('service'),
    title: svc.title || '',
    desc: svc.desc || '',
    src: svc.src || '',
    gallery: Array.isArray(svc.gallery) ? [...svc.gallery] : [],
  }));

const normalizeBbtsConfig = (data = {}) => {
  const heroBullets = Array.isArray(data.hero?.bullets)
    ? [...data.hero.bullets]
    : Array.isArray(defaultBbtsConfig.hero?.bullets)
      ? [...defaultBbtsConfig.hero.bullets]
      : [];

  return {
    ...defaultBbtsConfig,
    ...data,
    siteTitle: data.siteTitle || defaultBbtsConfig.siteTitle || defaultBbtsConfig.siteName,
    servicesTitle: data.servicesTitle || defaultBbtsConfig.servicesTitle || 'Soluciones destacadas',
    hero: {
      ...defaultBbtsConfig.hero,
      ...(data.hero || {}),
      bullets: heroBullets,
    },
    services:
      Array.isArray(data.services) && data.services.length
        ? cloneServices(data.services)
        : cloneServices(defaultBbtsConfig.services || []),
    login: {
      ...defaultBbtsConfig.login,
      ...(data.login || {}),
    },
    form: {
      ...defaultBbtsConfig.form,
      ...(data.form || {}),
    },
    calendar: {
      ...defaultBbtsConfig.calendar,
      ...(data.calendar || {}),
    },
    whatsappModal: {
      ...(defaultBbtsConfig.whatsappModal || {}),
      ...(data.whatsappModal || {}),
    },
    theme: {
      ...(defaultBbtsConfig.theme || {}),
      ...(data.theme || {}),
    },
  };
};

const isVideoUrl = (url = '') => /\.(mp4|mov|m4v|webm|ogg)$/i.test(url);
const isPdfUrl = (url = '') => /\.pdf(\?.*)?$/i.test(url);

const LandingAdmin = () => {
  const TAB_BOOKME = 'bookme';
  const TAB_BBTS = 'bbts';
  const [activeTab, setActiveTab] = useState(TAB_BOOKME);

  const { settings: bookmeSettings, loading: loadingBookme, error: bookmeError } = useLandingSettings();
  const [bookmeForm, setBookmeForm] = useState(bookmeSettings);
  const [bookmeSaving, setBookmeSaving] = useState(false);
  const [bookmeStatus, setBookmeStatus] = useState('');
  const [bookmeGalleryUrl, setBookmeGalleryUrl] = useState('');

  const [bbtsForm, setBbtsForm] = useState(() => normalizeBbtsConfig(defaultBbtsConfig));
  const [bbtsLoading, setBbtsLoading] = useState(true);
  const [bbtsError, setBbtsError] = useState(null);
  const [bbtsSaving, setBbtsSaving] = useState(false);
  const [bbtsStatus, setBbtsStatus] = useState('');

  const [uploadingHint, setUploadingHint] = useState('');
  const [serviceGalleryInputs, setServiceGalleryInputs] = useState({});

  useEffect(() => {
    setBookmeForm({
      hero: bookmeSettings.hero || {},
      features: Array.isArray(bookmeSettings.features) ? bookmeSettings.features : [],
      testimonials: Array.isArray(bookmeSettings.testimonials) ? bookmeSettings.testimonials : [],
      gallery: Array.isArray(bookmeSettings.gallery) ? bookmeSettings.gallery : [],
      whatsapp: bookmeSettings.whatsapp || {},
    });
  }, [bookmeSettings]);

  useEffect(() => {
    let cancelled = false;
    const fetchBbts = async () => {
      setBbtsLoading(true);
      try {
        const docRef = doc(db, 'config', 'landing');
        const snap = await getDoc(docRef);
        if (!snap.exists()) {
          await setDoc(docRef, defaultBbtsConfig);
          if (!cancelled) setBbtsForm(normalizeBbtsConfig(defaultBbtsConfig));
          return;
        }
        const data = snap.data() || {};
        if (!cancelled) {
          setBbtsForm(normalizeBbtsConfig(data));
        }
      } catch (err) {
        console.error('❌ Error cargando landing principal:', err);
        if (!cancelled) {
          setBbtsError(err);
          setBbtsForm(normalizeBbtsConfig(defaultBbtsConfig));
        }
      } finally {
        if (!cancelled) setBbtsLoading(false);
      }
    };

    fetchBbts();
    return () => {
      cancelled = true;
    };
  }, []);

  const uploadAsset = async (file, pathPrefix = 'landing/uploads') => {
    const safePath = `${pathPrefix}/${Date.now()}-${file.name}`;
    const storageRef = ref(storage, safePath);
    const snapshot = await uploadBytes(storageRef, file);
    return getDownloadURL(snapshot.ref);
  };

  const handleBookmeHeroChange = (field, value) => {
    setBookmeForm((prev) => ({
      ...prev,
      hero: {
        ...(prev.hero || {}),
        [field]: value,
      },
    }));
  };

  const handleBookmeWhatsappChange = (field, value) => {
    setBookmeForm((prev) => ({
      ...prev,
      whatsapp: {
        ...(prev.whatsapp || {}),
        [field]: value,
      },
    }));
  };

  const updateFeature = (index, field, value) => {
    setBookmeForm((prev) => {
      const nextFeatures = [...(prev.features || [])];
      nextFeatures[index] = {
        ...nextFeatures[index],
        [field]: value,
      };
      return { ...prev, features: nextFeatures };
    });
  };

  const addFeature = () => {
    setBookmeForm((prev) => ({
      ...prev,
      features: [...(prev.features || []), emptyFeature()],
    }));
  };

  const removeFeature = (index) => {
    setBookmeForm((prev) => ({
      ...prev,
      features: (prev.features || []).filter((_, idx) => idx !== index),
    }));
  };

  const addTestimonial = () => {
    setBookmeForm((prev) => ({
      ...prev,
      testimonials: [...(prev.testimonials || []), emptyTestimonial()],
    }));
  };

  const removeTestimonial = (index) => {
    setBookmeForm((prev) => ({
      ...prev,
      testimonials: (prev.testimonials || []).filter((_, idx) => idx !== index),
    }));
  };

  const updateTestimonial = (index, field, value) => {
    setBookmeForm((prev) => {
      const testimonials = [...(prev.testimonials || [])];
      testimonials[index] = { ...testimonials[index], [field]: value };
      return { ...prev, testimonials };
    });
  };

  const handleBookmeGalleryUpload = async (files) => {
    if (!files || !files.length) return;
    setUploadingHint('Subiendo imágenes de la galería (BookMe)...');
    try {
      const uploaded = [];
      for (const file of files) {
        const url = await uploadAsset(file, 'landing/bookme/gallery');
        uploaded.push(url);
      }
      setBookmeForm((prev) => ({
        ...prev,
        gallery: [...(prev.gallery || []), ...uploaded],
      }));
      setBookmeStatus('✅ Galería actualizada.');
    } catch (err) {
      console.error('❌ Error subiendo galería BookMe', err);
      setBookmeStatus('❌ Error subiendo imágenes. Intenta nuevamente.');
    } finally {
      setUploadingHint('');
    }
  };

  const removeBookmeGalleryItem = (index) => {
    setBookmeForm((prev) => ({
      ...prev,
      gallery: (prev.gallery || []).filter((_, idx) => idx !== index),
    }));
  };

  const handleFeatureMediaUpload = async (index, file) => {
    if (!file) return;
    setUploadingHint('Subiendo imagen de funcionalidad (BookMe)...');
    try {
      const url = await uploadAsset(file, `landing/bookme/features/${bookmeForm.features?.[index]?.id || 'feature'}`);
      updateFeature(index, 'media', url);
    } catch (err) {
      console.error('❌ Error subiendo imagen de funcionalidad', err);
      setBookmeStatus('❌ No se pudo subir la imagen.');
    } finally {
      setUploadingHint('');
    }
  };

  const handleBookmeGalleryUrlAdd = () => {
    if (!bookmeGalleryUrl.trim()) return;
    setBookmeForm((prev) => ({
      ...prev,
      gallery: [...(prev.gallery || []), bookmeGalleryUrl.trim()],
    }));
    setBookmeGalleryUrl('');
  };

  const handleBookmeSave = async () => {
    setBookmeSaving(true);
    setBookmeStatus('');
    try {
      const payload = {
        hero: bookmeForm.hero || {},
        whatsapp: bookmeForm.whatsapp || {},
        features: (bookmeForm.features || []).map((feature, idx) => ({
          id: feature.id || randomId(`feature-${idx}`),
          title: feature.title || '',
          desc: feature.desc || '',
          mediaType: feature.mediaType || 'image',
          media: feature.media || '',
        })),
        testimonials: (bookmeForm.testimonials || []).map((t, idx) => ({
          id: t.id || randomId(`testimonial-${idx}`),
          name: t.name || '',
          text: t.text || '',
        })),
        gallery: bookmeForm.gallery || [],
      };

      const landingRef = doc(db, 'settings', 'landing');
      await setDoc(landingRef, payload, { merge: true });
      setBookmeStatus('✅ Cambios guardados correctamente.');
    } catch (err) {
      console.error('❌ Error guardando landing BookMe', err);
      setBookmeStatus('❌ No se pudieron guardar los cambios.');
    } finally {
      setBookmeSaving(false);
    }
  };

  const handleBbtsField = (field, value) => {
    setBbtsForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleBbtsHeroChange = (field, value) => {
    setBbtsForm((prev) => ({
      ...prev,
      hero: {
        ...(prev.hero || {}),
        [field]: value,
      },
    }));
  };

  const handleBbtsHeroBulletsChange = (value) => {
    const bullets = value
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    setBbtsForm((prev) => ({
      ...prev,
      hero: {
        ...(prev.hero || {}),
        bullets,
      },
    }));
  };

  const handleBbtsWhatsappChange = (field, value) => {
    setBbtsForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleBbtsModalChange = (field, value) => {
    setBbtsForm((prev) => ({
      ...prev,
      whatsappModal: {
        ...(prev.whatsappModal || {}),
        [field]: value,
      },
    }));
  };

  const handleBbtsLoginChange = (field, value) => {
    setBbtsForm((prev) => ({
      ...prev,
      login: {
        ...(prev.login || {}),
        [field]: value,
      },
    }));
  };

  const handleBbtsFormTextsChange = (field, value) => {
    setBbtsForm((prev) => ({
      ...prev,
      form: {
        ...(prev.form || {}),
        [field]: value,
      },
    }));
  };

  const handleBbtsCalendarChange = (field, value) => {
    setBbtsForm((prev) => ({
      ...prev,
      calendar: {
        ...(prev.calendar || {}),
        [field]: value,
      },
    }));
  };

  const handleThemeChange = (field, value) => {
    setBbtsForm((prev) => ({
      ...prev,
      theme: {
        ...(prev.theme || {}),
        [field]: value,
      },
    }));
  };

  const updateBbtsService = (index, field, value) => {
    setBbtsForm((prev) => {
      const services = [...(prev.services || [])];
      services[index] = {
        ...services[index],
        [field]: value,
      };
      return { ...prev, services };
    });
  };

  const addBbtsService = () => {
    setBbtsForm((prev) => ({
      ...prev,
      services: [...(prev.services || []), emptyService()],
    }));
  };

  const removeBbtsService = (index) => {
    setBbtsForm((prev) => ({
      ...prev,
      services: (prev.services || []).filter((_, idx) => idx !== index),
    }));
  };

  const handleBbtsLogoUpload = async (file) => {
    if (!file) return;
    setUploadingHint('Subiendo logo principal...');
    try {
      const url = await uploadAsset(file, 'landing/bbts/logo');
      handleBbtsField('logo', url);
    } catch (err) {
      console.error('❌ Error subiendo logo', err);
      setBbtsStatus('❌ No se pudo subir el logo.');
    } finally {
      setUploadingHint('');
    }
  };

  const handleBbtsServiceImageUpload = async (index, file) => {
    if (!file) return;
    setUploadingHint('Subiendo imagen del servicio...');
    try {
      const url = await uploadAsset(file, `landing/bbts/services/${bbtsForm.services?.[index]?.id || 'service'}`);
      updateBbtsService(index, 'src', url);
    } catch (err) {
      console.error('❌ Error subiendo imagen del servicio', err);
      setBbtsStatus('❌ No se pudo subir la imagen del servicio.');
    } finally {
      setUploadingHint('');
    }
  };

  const getServiceKey = (service, index) => service.id || `service-${index}`;

  const handleBbtsServiceGalleryUpload = async (index, files) => {
    if (!files || !files.length) return;
    setUploadingHint('Subiendo galería del servicio...');
    try {
      const uploaded = [];
      for (const file of files) {
        const url = await uploadAsset(file, `landing/bbts/services/${bbtsForm.services?.[index]?.id || 'service'}/gallery`);
        uploaded.push(url);
      }
      setBbtsForm((prev) => {
        const services = [...(prev.services || [])];
        const gallery = services[index].gallery || [];
        services[index] = {
          ...services[index],
          gallery: [...gallery, ...uploaded],
        };
        return { ...prev, services };
      });
    } catch (err) {
      console.error('❌ Error subiendo galería del servicio', err);
      setBbtsStatus('❌ No se pudo subir la galería.');
    } finally {
      setUploadingHint('');
    }
  };

  const handleServiceGalleryUrlChange = (serviceKey, value) => {
    setServiceGalleryInputs((prev) => ({
      ...prev,
      [serviceKey]: value,
    }));
  };

  const addServiceGalleryUrl = (index) => {
    const service = bbtsForm.services?.[index];
    if (!service) return;
    const key = getServiceKey(service, index);
    const url = (serviceGalleryInputs[key] || '').trim();
    if (!url) return;

    setBbtsForm((prev) => {
      const services = [...(prev.services || [])];
      const gallery = services[index].gallery || [];
      services[index] = {
        ...services[index],
        gallery: [...gallery, url],
      };
      return { ...prev, services };
    });

    setServiceGalleryInputs((prev) => ({
      ...prev,
      [key]: '',
    }));
  };

  const removeBbtsServiceGalleryItem = (serviceIndex, imgIndex) => {
    setBbtsForm((prev) => {
      const services = [...(prev.services || [])];
      services[serviceIndex] = {
        ...services[serviceIndex],
        gallery: (services[serviceIndex].gallery || []).filter((_, idx) => idx !== imgIndex),
      };
      return { ...prev, services };
    });
  };

  const handleBbtsSave = async () => {
    setBbtsSaving(true);
    setBbtsStatus('');
    try {
      const payload = {
        siteTitle: bbtsForm.siteTitle || '',
        siteName: bbtsForm.siteName || defaultBbtsConfig.siteName,
        logo: bbtsForm.logo || '',
        googleTagId: bbtsForm.googleTagId || defaultBbtsConfig.googleTagId,
        redirectUTM: bbtsForm.redirectUTM || '',
        redirectWhatsAppLink: bbtsForm.redirectWhatsAppLink || '',
        servicesTitle: bbtsForm.servicesTitle || '',
        whatsappPhone: bbtsForm.whatsappPhone || defaultBbtsConfig.whatsappPhone,
        whatsappMessage: bbtsForm.whatsappMessage || defaultBbtsConfig.whatsappMessage,
        hero: {
          ...(bbtsForm.hero || {}),
          bullets: (bbtsForm.hero?.bullets || []).filter(Boolean),
        },
        services: (bbtsForm.services || []).map((service, idx) => ({
          id: service.id || randomId(`service-${idx}`),
          title: service.title || '',
          desc: service.desc || '',
          src: service.src || '',
          gallery: (service.gallery || []).filter(Boolean),
        })),
        login: bbtsForm.login || {},
        form: bbtsForm.form || {},
        calendar: bbtsForm.calendar || {},
        whatsappModal: bbtsForm.whatsappModal || {},
        theme: {
          ...(defaultBbtsConfig.theme || {}),
          ...(bbtsForm.theme || {}),
        },
      };
      const docRef = doc(db, 'config', 'landing');
      await setDoc(docRef, payload, { merge: true });
      setBbtsStatus('✅ Landing principal actualizada.');
    } catch (err) {
      console.error('❌ Error guardando landing principal', err);
      setBbtsStatus('❌ No se pudo guardar la configuración.');
    } finally {
      setBbtsSaving(false);
    }
  };

  const renderBookmeEditor = () => (
    <>
      {bookmeError && (
        <p className="error-message">⚠️ No se pudo cargar la configuración. Se muestran los valores predeterminados.</p>
      )}
      {loadingBookme && <p>Cargando configuración de BookMe...</p>}

      <section className="card">
        <h3>Hero principal</h3>
        <label>Título
          <input type="text" value={bookmeForm.hero?.title || ''} onChange={(e) => handleBookmeHeroChange('title', e.target.value)} />
        </label>
        <label>Texto destacado
          <input type="text" value={bookmeForm.hero?.highlight || ''} onChange={(e) => handleBookmeHeroChange('highlight', e.target.value)} />
        </label>
        <label>Descripción
          <textarea value={bookmeForm.hero?.description || ''} onChange={(e) => handleBookmeHeroChange('description', e.target.value)} />
        </label>
        <div className="row two-columns">
          <label>Texto del CTA
            <input type="text" value={bookmeForm.hero?.ctaText || ''} onChange={(e) => handleBookmeHeroChange('ctaText', e.target.value)} />
          </label>
          <label>Enlace del CTA
            <input type="text" value={bookmeForm.hero?.ctaLink || ''} onChange={(e) => handleBookmeHeroChange('ctaLink', e.target.value)} />
          </label>
        </div>
      </section>

      <section className="card">
        <h3>Colores del tema</h3>
        <div className="row two-columns">
          <label>Primario
            <input
              type="color"
              value={bbtsForm.theme?.primary || '#f97316'}
              onChange={(e) => handleThemeChange('primary', e.target.value)}
            />
          </label>
          <label>Primario (hover)
            <input
              type="color"
              value={bbtsForm.theme?.primaryDark || '#ea580c'}
              onChange={(e) => handleThemeChange('primaryDark', e.target.value)}
            />
          </label>
        </div>
        <div className="row two-columns">
          <label>Color de acento
            <input
              type="color"
              value={bbtsForm.theme?.accent || '#111827'}
              onChange={(e) => handleThemeChange('accent', e.target.value)}
            />
          </label>
          <label>Resaltado
            <input
              type="color"
              value={bbtsForm.theme?.highlight || '#ffedd5'}
              onChange={(e) => handleThemeChange('highlight', e.target.value)}
            />
          </label>
        </div>
        <div className="row two-columns">
          <label>Gradiente (inicio)
            <input
              type="color"
              value={bbtsForm.theme?.gradientFrom || '#fff7ed'}
              onChange={(e) => handleThemeChange('gradientFrom', e.target.value)}
            />
          </label>
          <label>Gradiente (fin)
            <input
              type="color"
              value={bbtsForm.theme?.gradientTo || '#ffffff'}
              onChange={(e) => handleThemeChange('gradientTo', e.target.value)}
            />
          </label>
        </div>
        <div className="row two-columns">
          <label>Fondo de tarjetas
            <input
              type="color"
              value={bbtsForm.theme?.cardBg || '#ffffff'}
              onChange={(e) => handleThemeChange('cardBg', e.target.value)}
            />
          </label>
          <label>Borde de tarjetas
            <input
              type="color"
              value={bbtsForm.theme?.cardBorder || '#f3f4f6'}
              onChange={(e) => handleThemeChange('cardBorder', e.target.value)}
            />
          </label>
        </div>
      </section>

      <section className="card">
        <div className="card-header">
          <h3>Funcionalidades destacadas</h3>
          <button type="button" onClick={addFeature}>+ Agregar funcionalidad</button>
        </div>
        {(bookmeForm.features || []).map((feature, index) => (
          <div key={feature.id || index} className="feature-block">
            <div className="row two-columns">
              <label>Título
                <input type="text" value={feature.title || ''} onChange={(e) => updateFeature(index, 'title', e.target.value)} />
              </label>
              <label>Tipo de media
                <select value={feature.mediaType || 'image'} onChange={(e) => updateFeature(index, 'mediaType', e.target.value)}>
                  <option value="image">Imagen</option>
                  <option value="video">Video</option>
                </select>
              </label>
            </div>
            <label>Descripción
              <textarea value={feature.desc || ''} onChange={(e) => updateFeature(index, 'desc', e.target.value)} />
            </label>
            <label>URL del recurso
              <input
                type="text"
                value={feature.media || ''}
                onChange={(e) => updateFeature(index, 'media', e.target.value)}
                placeholder={feature.mediaType === 'video' ? 'https://...' : 'Sube una imagen o pega la URL'}
              />
            </label>
            {feature.mediaType === 'image' && (
              <div>
                <input type="file" accept="image/*" onChange={(e) => handleFeatureMediaUpload(index, e.target.files?.[0])} />
                {feature.media && <img src={feature.media} alt={feature.title || 'feature'} className="preview-image" />}
              </div>
            )}
            <button type="button" className="btn-delete" onClick={() => removeFeature(index)}>Eliminar</button>
          </div>
        ))}
      </section>

      <section className="card">
        <div className="card-header">
          <h3>Testimonios</h3>
          <button type="button" onClick={addTestimonial}>+ Agregar testimonio</button>
        </div>
        {(bookmeForm.testimonials || []).map((t, index) => (
          <div key={t.id || index} className="testimonial-block">
            <label>Nombre
              <input type="text" value={t.name || ''} onChange={(e) => updateTestimonial(index, 'name', e.target.value)} />
            </label>
            <label>Comentario
              <textarea value={t.text || ''} onChange={(e) => updateTestimonial(index, 'text', e.target.value)} />
            </label>
            <button type="button" className="btn-delete" onClick={() => removeTestimonial(index)}>Eliminar</button>
          </div>
        ))}
      </section>

      <section className="card">
        <h3>Galería</h3>
        <input type="file" accept="image/*" multiple onChange={(e) => handleBookmeGalleryUpload(e.target.files)} />
        <div className="row two-columns">
          <label>Agregar imagen por URL
            <input type="text" value={bookmeGalleryUrl} onChange={(e) => setBookmeGalleryUrl(e.target.value)} placeholder="https://..." />
          </label>
          <button type="button" onClick={handleBookmeGalleryUrlAdd}>Añadir a galería</button>
        </div>
        <div className="gallery-grid">
          {(bookmeForm.gallery || []).map((img, index) => (
            <div key={`${img}-${index}`} className="gallery-item">
              <img src={img} alt={`gallery-${index}`} />
              <button type="button" onClick={() => removeBookmeGalleryItem(index)}>×</button>
            </div>
          ))}
        </div>
      </section>

      <section className="card">
        <h3>Botón de WhatsApp</h3>
        <label>Número (sin +)
          <input type="text" value={bookmeForm.whatsapp?.number || ''} onChange={(e) => handleBookmeWhatsappChange('number', e.target.value)} />
        </label>
        <label>Mensaje predeterminado
          <textarea value={bookmeForm.whatsapp?.message || ''} onChange={(e) => handleBookmeWhatsappChange('message', e.target.value)} />
        </label>
        <div className="row two-columns">
          <label>Texto en móvil
            <input type="text" value={bookmeForm.whatsapp?.labelMobile || ''} onChange={(e) => handleBookmeWhatsappChange('labelMobile', e.target.value)} />
          </label>
          <label>Texto en desktop
            <input type="text" value={bookmeForm.whatsapp?.labelDesktop || ''} onChange={(e) => handleBookmeWhatsappChange('labelDesktop', e.target.value)} />
          </label>
        </div>
      </section>

      {bookmeStatus && <p>{bookmeStatus}</p>}

      <button type="button" className="save-btn" onClick={handleBookmeSave} disabled={bookmeSaving}>
        {bookmeSaving ? 'Guardando...' : 'Guardar cambios BookMe'}
      </button>
    </>
  );

  const renderBbtsEditor = () => (
    <>
      {bbtsError && (
        <p className="error-message">⚠️ No se pudo cargar la landing principal. Se muestran los valores locales.</p>
      )}
      {bbtsLoading && <p>Cargando datos de bbtspr.com...</p>}

      <section className="card">
        <h3>Identidad del sitio</h3>
        <label>Título para SEO
          <input type="text" value={bbtsForm.siteTitle || ''} onChange={(e) => handleBbtsField('siteTitle', e.target.value)} />
        </label>
        <label>Nombre del sitio
          <input type="text" value={bbtsForm.siteName || ''} onChange={(e) => handleBbtsField('siteName', e.target.value)} />
        </label>
        <label>Logo
          <input type="text" value={bbtsForm.logo || ''} onChange={(e) => handleBbtsField('logo', e.target.value)} placeholder="https://..." />
        </label>
        <input type="file" accept="image/*" onChange={(e) => handleBbtsLogoUpload(e.target.files?.[0])} />
        {bbtsForm.logo && <img src={bbtsForm.logo} alt="Logo" className="preview-image" />}
        <label>Google Tag ID
          <input type="text" value={bbtsForm.googleTagId || ''} onChange={(e) => handleBbtsField('googleTagId', e.target.value)} />
        </label>
        <div className="row two-columns">
          <label>UTM por defecto
            <input type="text" value={bbtsForm.redirectUTM || ''} onChange={(e) => handleBbtsField('redirectUTM', e.target.value)} />
          </label>
          <label>Link WhatsApp de redirección
            <input type="text" value={bbtsForm.redirectWhatsAppLink || ''} onChange={(e) => handleBbtsField('redirectWhatsAppLink', e.target.value)} />
          </label>
        </div>
      </section>

      <section className="card">
        <h3>Hero principal BBTS</h3>
        <label>Título
          <input type="text" value={bbtsForm.hero?.title || ''} onChange={(e) => handleBbtsHeroChange('title', e.target.value)} />
        </label>
        <label>Subtítulo
          <textarea value={bbtsForm.hero?.subtitle || ''} onChange={(e) => handleBbtsHeroChange('subtitle', e.target.value)} />
        </label>
        <label>Bullets (una por línea)
          <textarea value={(bbtsForm.hero?.bullets || []).join('\n')} onChange={(e) => handleBbtsHeroBulletsChange(e.target.value)} />
        </label>
        <label>Texto CTA
          <input type="text" value={bbtsForm.hero?.cta || ''} onChange={(e) => handleBbtsHeroChange('cta', e.target.value)} />
        </label>
      </section>

      <section className="card">
        <div className="card-header">
          <h3>Servicios destacados</h3>
          <button type="button" onClick={addBbtsService}>+ Agregar servicio</button>
        </div>
        <label>Título de la sección
          <input type="text" value={bbtsForm.servicesTitle || ''} onChange={(e) => handleBbtsField('servicesTitle', e.target.value)} />
        </label>
        {(bbtsForm.services || []).map((service, index) => (
          <div key={service.id || index} className="feature-block">
            <div className="row two-columns">
              <label>Título
                <input type="text" value={service.title || ''} onChange={(e) => updateBbtsService(index, 'title', e.target.value)} />
              </label>
              <label>Imagen principal
                <input type="text" value={service.src || ''} onChange={(e) => updateBbtsService(index, 'src', e.target.value)} placeholder="https://..." />
              </label>
            </div>
            <input type="file" accept="image/*" onChange={(e) => handleBbtsServiceImageUpload(index, e.target.files?.[0])} />
            {service.src && <img src={service.src} alt={service.title || 'Servicio'} className="preview-image" />}
            <label>Descripción
              <textarea value={service.desc || ''} onChange={(e) => updateBbtsService(index, 'desc', e.target.value)} />
            </label>
            <div className="gallery-control">
              <label>Galería del servicio</label>
              <div className="gallery-url-input">
                <input
                  type="text"
                  value={serviceGalleryInputs[getServiceKey(service, index)] || ''}
                  onChange={(e) => handleServiceGalleryUrlChange(getServiceKey(service, index), e.target.value)}
                  placeholder="https://mi-imagen.jpg"
                />
                <button type="button" onClick={() => addServiceGalleryUrl(index)}>
                  Agregar URL
                </button>
              </div>
            </div>
            {service.gallery?.length > 0 && (
              <div className="gallery-grid">
                {service.gallery.map((item, i) => {
                  const video = isVideoUrl(item);
                  const pdf = isPdfUrl(item);
                  return (
                    <div key={`${item}-${i}`} className={`gallery-item${pdf ? ' gallery-item-doc' : ''}`}>
                      {video ? (
                        <video src={item} controls className="gallery-video" />
                      ) : pdf ? (
                        <div className="doc-preview">
                          <span>PDF</span>
                          <a href={item} target="_blank" rel="noopener noreferrer">Ver archivo</a>
                        </div>
                      ) : (
                        <img src={item} alt={`gallery-${i}`} />
                      )}
                      <button type="button" onClick={() => removeBbtsServiceGalleryItem(index, i)}>×</button>
                    </div>
                  );
                })}
              </div>
            )}
            <label className="upload-label">
              Subir archivos
              <input
                type="file"
                accept="image/*,video/*,application/pdf"
                multiple
                onChange={(e) => handleBbtsServiceGalleryUpload(index, e.target.files)}
              />
            </label>
            <button type="button" className="btn-delete" onClick={() => removeBbtsService(index)}>Eliminar servicio</button>
          </div>
        ))}
      </section>

      <section className="card">
        <h3>Textos auxiliares</h3>
        <div className="row two-columns">
          <label>Login - texto
            <input type="text" value={bbtsForm.login?.text || ''} onChange={(e) => handleBbtsLoginChange('text', e.target.value)} />
          </label>
          <label>Login - botón
            <input type="text" value={bbtsForm.login?.button || ''} onChange={(e) => handleBbtsLoginChange('button', e.target.value)} />
          </label>
        </div>
        <div className="row two-columns">
          <label>Formulario - abrir
            <input type="text" value={bbtsForm.form?.showText || ''} onChange={(e) => handleBbtsFormTextsChange('showText', e.target.value)} />
          </label>
          <label>Formulario - cerrar
            <input type="text" value={bbtsForm.form?.hideText || ''} onChange={(e) => handleBbtsFormTextsChange('hideText', e.target.value)} />
          </label>
        </div>
        <div className="row two-columns">
          <label>Calendario - título
            <input type="text" value={bbtsForm.calendar?.title || ''} onChange={(e) => handleBbtsCalendarChange('title', e.target.value)} />
          </label>
          <label>Calendario flotante
            <input type="text" value={bbtsForm.calendar?.floatTitle || ''} onChange={(e) => handleBbtsCalendarChange('floatTitle', e.target.value)} />
          </label>
        </div>
      </section>

      <section className="card">
        <h3>WhatsApp general</h3>
        <div className="row two-columns">
          <label>Número (sin +)
            <input type="text" value={bbtsForm.whatsappPhone || ''} onChange={(e) => handleBbtsWhatsappChange('whatsappPhone', e.target.value)} />
          </label>
          <label>Mensaje por defecto
            <input type="text" value={bbtsForm.whatsappMessage || ''} onChange={(e) => handleBbtsWhatsappChange('whatsappMessage', e.target.value)} />
          </label>
        </div>
        <label>Texto del botón modal
          <input type="text" value={bbtsForm.whatsappModal?.buttonText || ''} onChange={(e) => handleBbtsModalChange('buttonText', e.target.value)} />
        </label>
      </section>

      {bbtsStatus && <p>{bbtsStatus}</p>}

      <button type="button" className="save-btn" onClick={handleBbtsSave} disabled={bbtsSaving}>
        {bbtsSaving ? 'Guardando...' : 'Guardar landing principal'}
      </button>
    </>
  );

  return (
    <div className="landing-admin">
      <h2>Editor de Landing Pages</h2>
      <p className="text-sm text-gray-500 mb-4">Selecciona la vista que deseas actualizar.</p>
      <div className="tab-switcher">
        <button
          type="button"
          className={activeTab === TAB_BOOKME ? 'active' : ''}
          onClick={() => setActiveTab(TAB_BOOKME)}
        >
          BookMe (Landing promocional)
        </button>
        <button
          type="button"
          className={activeTab === TAB_BBTS ? 'active' : ''}
          onClick={() => setActiveTab(TAB_BBTS)}
        >
          bbtspr.com (Landing principal)
        </button>
      </div>
      {uploadingHint && <p>{uploadingHint}</p>}
      {activeTab === TAB_BOOKME ? renderBookmeEditor() : renderBbtsEditor()}
    </div>
  );
};

export default LandingAdmin;
